import { UploadOutlined } from "@ant-design/icons";
import { Button, Upload, message } from "antd";
import type { UploadChangeParam, UploadFile } from "antd/es/upload/interface";
import { memo, useCallback, useEffect, useState } from "react";
import type { OpportunityFile } from "types/opportunity";
import { getToken } from "utils/auth";
import "./OpportunityFiles.scss";
import { settings } from "settings";

const defaultUploadUrl = `${import.meta.env.VITE_API_URL}/v1/admin/opportunity/upload`;
const allowExtensions = ["pdf", "png", "jpg", "jpeg", "gif", "webp", "doc", "docx", "xls", "xlsx", "csv", "txt", "zip", "rar"];

type OpportunityFilesProps = {
  value: OpportunityFile[];
  readOnly?: boolean;
  uploadUrl?: string;
  onChange: (files: OpportunityFile[]) => void;
};

const toUploadList = (files: OpportunityFile[]): UploadFile[] =>
  files.map((file, index) => ({
    uid: `${file.url}-${index}`,
    name: file.name || file.url,
    status: "done",
    url: file.url,
  }));

const doneFiles = (fileList: UploadFile[]): OpportunityFile[] =>
  fileList
    .map((file) => {
      const response = file.response as { data?: { path?: string } } | undefined;
      const url = file.url || response?.data?.path || "";
      return {
        name: file.name,
        url,
        status: file.status,
      };
    })
    .filter((file) => file.status === "done" && file.url)
    .map((file) => ({ name: file.name, url: file.url }));

export const OpportunityFiles = memo(({ value, readOnly, uploadUrl = defaultUploadUrl, onChange }: OpportunityFilesProps) => {
  const [fileList, setFileList] = useState<UploadFile[]>(() => toUploadList(value));
  const valueKey = value.map((file) => file.url).join("|");

  useEffect(() => {
    setFileList((current) => {
      if (current.some((file) => file.status === "uploading")) {
        return current;
      }
      return toUploadList(value);
    });
  }, [value, valueKey]);

  const beforeUpload = useCallback((file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (!allowExtensions.includes(ext)) {
      message.error("Định dạng file không hỗ trợ");
      return Upload.LIST_IGNORE;
    }
    return true;
  }, []);

  const handleChange = useCallback(
    (info: UploadChangeParam) => {
      setFileList(info.fileList);
      onChange(doneFiles(info.fileList));
      if (info.file.status === "error") {
        message.error("Upload thất bại");
      }
    },
    [onChange],
  );

  const preview = useCallback((file: UploadFile) => {
    const response = file.response as { data?: { path?: string } } | undefined;
    const url = file.url || response?.data?.path;
    if (url) window.open(url, "_blank");
  }, []);

  return (
    <Upload
      className="opportunityFiles"
      multiple
      action={uploadUrl}
      headers={{ token: getToken(), version: settings.version }}
      accept={allowExtensions.map((ext) => `.${ext}`).join(",")}
      fileList={fileList}
      beforeUpload={beforeUpload}
      onChange={handleChange}
      onPreview={preview}
      disabled={readOnly}
    >
      {!readOnly && <Button icon={<UploadOutlined />}>Tải file</Button>}
    </Upload>
  );
});

OpportunityFiles.displayName = "OpportunityFiles";
